import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

/**
 * Firm-controlled automation settings.
 *
 * Court LOR auto-send used to be an environment variable, which meant only the developers could
 * change it. Lloyd asked for the switch himself (Jul 18 2026), so it lives in the database and is
 * flipped here by any manager holding "Can manage firm settings".
 *
 * The unusable-email count sits next to the switch deliberately. Auto-send is only safe once court
 * contact data is clean, and that number is the honest measure of how clean it currently is.
 */

interface AutomationSettings {
  courtLorAutoSend: boolean;
  appearanceAutoNotify: boolean;
  bondCourtAutoSend: boolean;
  /*
   * Everything below is optional, and the fields above are not.
   *
   * They were all added on 8 Sep, and an API that has not been restarted or is mid-rolling-deploy
   * answers without them. Typed as required, `settings.discoveryApproval.approved` then throws and
   * takes the whole settings page down over a section the reader may not even care about. Optional
   * here, guarded at every use, and a missing field simply hides its control.
   */
  portalInviteSend?: boolean;
  /** Added 28 Sep. Optional for the same reason as the rest of this block. */
  motionCourtAutoSend?: boolean;
  /** Added 29 Sep. Seeded ON at Lloyd's request; optional like the rest of this block. */
  acceptanceCourtAutoSend?: boolean;
  /** Added 28 Sep. Optional for the same reason as the rest of this block. */
  offerAutoStageMove?: boolean;
  staleCaseCourtAutoSend?: boolean;
  messagesClientResend?: boolean;
  /** Not a send switch: it decides whether staff are shown a failure-to-appear figure at all. */
  failureToAppearFee?: boolean;
  /** Whether the CDL Consultants mailbox is swept into cases. Answers depend on cdlAutoReply. */
  cdlEmailToCase?: boolean;
  /** Whether a matched CDL status request is answered without a person reading it first. */
  cdlAutoReply?: boolean;
  /** Blank means the shipped sentence is in use. */
  messagesResendPreface?: string;
  messagesResendPrefaceDefault?: string;
  /** The 39.14 wording this build carries, and whether the firm has signed it off. */
  discoveryApproval?: {
    approved: boolean;
    currentRevision: number;
    approvedRevision: number | null;
    explanation: string;
  };
  canManage: boolean;
  updatedBy: string | null;
  updatedAt: string | null;
  courtsWithUnusableEmail: number;
  courtsTotal: number;
}

const ENDPOINT = (process.env.API_HOST || '') + '/api/v1/manager/settings/automation';

const AutomationSettingsPanel: React.FC = () => {
  const [settings, setSettings] = useState<AutomationSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(ENDPOINT, { credentials: 'include' });
      if (!response.ok) {
        throw new Error('Failed to load automation settings');
      }
      setSettings((await response.json()) as AutomationSettings);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load automation settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /*
   * The wording is a text box, so it is held locally while it is being typed and written on blur.
   * A switch can save on every change because there is one keystroke; a sentence cannot, or every
   * character is a request and a half-typed line is briefly what the client would receive.
   */
  const [preface, setPreface] = useState('');
  useEffect(() => {
    if (settings) setPreface(settings.messagesResendPreface ?? '');
  }, [settings?.messagesResendPreface]);

  /*
   * Approving the 39.14 wording. Not a switch, and deliberately not undoable from here.
   *
   * The revision is sent explicitly rather than "approve whatever is current", so a person who read
   * the paragraphs on this page cannot accidentally sign off a newer deploy. The server refuses a
   * mismatch with a 409.
   */
  const approveDiscoveryWording = async () => {
    if (!settings?.discoveryApproval) return;
    setSaving(true);
    setError(null);
    try {
      const response = await fetch(ENDPOINT, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          approveDiscoveryRevision: settings.discoveryApproval?.currentRevision,
        }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || 'Failed to approve the wording');
      }
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve the wording');
    } finally {
      setSaving(false);
    }
  };

  const savePreface = async () => {
    if (!settings) return;
    const next = preface.trim();
    if (next === (settings.messagesResendPreface ?? '').trim()) return;

    setSaving(true);
    setError(null);
    try {
      const response = await fetch(ENDPOINT, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messagesResendPreface: next }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || 'Failed to save the wording');
      }
      await load();
    } catch (err) {
      // Put the stored value back. A box still showing what somebody typed, after the save failed,
      // reads as saved.
      setPreface(settings.messagesResendPreface ?? '');
      setError(err instanceof Error ? err.message : 'Failed to save the wording');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (
    key:
      | 'courtLorAutoSend'
      | 'appearanceAutoNotify'
      | 'bondCourtAutoSend'
      | 'motionCourtAutoSend'
      | 'acceptanceCourtAutoSend'
      | 'offerAutoStageMove'
      | 'staleCaseCourtAutoSend'
      | 'portalInviteSend'
      | 'messagesClientResend'
      | 'failureToAppearFee'
      | 'cdlEmailToCase'
      | 'cdlAutoReply',
    nextValue: boolean,
  ) => {
    if (!settings) return;

    // Optimistic, but reverted on failure — a switch that lies about whether mail is going out
    // is worse than one that is briefly slow.
    const previous = settings[key];
    setSettings({ ...settings, [key]: nextValue });
    setSaving(true);
    setError(null);

    try {
      const response = await fetch(ENDPOINT, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [key]: nextValue }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || 'Failed to update automation settings');
      }
      await load();
    } catch (err) {
      setSettings({ ...settings, [key]: previous });
      setError(err instanceof Error ? err.message : 'Failed to update automation settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Automation
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && !settings ? (
        <CircularProgress size={24} />
      ) : settings ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={settings.courtLorAutoSend}
                onChange={(e) => void handleToggle('courtLorAutoSend', e.target.checked)}
                disabled={!settings.canManage || saving}
              />
            }
            label="Send letters of representation to courts automatically on approval"
          />

          <Typography variant="body2" color="text.secondary">
            {settings.courtLorAutoSend
              ? 'Approved LORs are sent straight to courts that have a usable email address. Courts without one still route to the internal LOR mailbox.'
              : 'Approved LORs are saved as Outlook drafts for staff to review and send by hand.'}
          </Typography>

          {settings.courtsWithUnusableEmail > 0 && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {settings.courtsWithUnusableEmail} of {settings.courtsTotal} courts have a missing or
              placeholder email address. Their letters route to the internal LOR mailbox rather than
              to the court.
              <br />
              Note that this check cannot detect an address that is well-formed but simply wrong —
              those will be sent while auto-send is on.
            </Alert>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Lloyd, Aug 10: a notice saying the client must appear should mark the case and email
              them. Shipped off — the detection is a model reading a scan, and the output tells
              somebody to attend court. Off still records and still shows; it just does not send. */}
          <FormControlLabel
            control={
              <Switch
                checked={settings.appearanceAutoNotify}
                onChange={(e) => void handleToggle('appearanceAutoNotify', e.target.checked)}
                disabled={!settings.canManage || saving}
              />
            }
            label="Email clients automatically when a scanned notice says they must attend court"
          />

          <Typography variant="body2" color="text.secondary">
            {settings.appearanceAutoNotify
              ? 'When a pre-trial or other notice clearly states the client must attend, the case is marked and the client is emailed and texted without anyone reviewing it first.'
              : 'Notices are still read and the case is still marked, but nobody is emailed until a staff member confirms it on the case. Staff ticking it by hand always emails the client, whichever way this is set.'}
          </Typography>

          {!settings.appearanceAutoNotify && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Recommended while this is new. Watch what the notices are being read as for a week or
              two on the case screens, then turn this on once you are satisfied.
            </Alert>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Lloyd, Aug 1: "build all things on a draft basis for now with the ability to switch
              to automatic at a later date." A person still marks the bond either way — this only
              decides whether the prepared message leaves without a second person seeing it. */}
          <FormControlLabel
            control={
              <Switch
                checked={settings.bondCourtAutoSend}
                onChange={(e) => void handleToggle('bondCourtAutoSend', e.target.checked)}
                disabled={!settings.canManage || saving}
              />
            }
            label="Send bonds to courts automatically once a reviewer marks them"
          />

          <Typography variant="body2" color="text.secondary">
            {settings.bondCourtAutoSend
              ? 'A bond a reviewer marks is emailed straight to the court, if that court has a usable email address. Nobody sees it before it goes.'
              : 'A bond a reviewer marks is prepared, addressed and attached, then waits in the Court Communication drafts for someone to send. Marking a bond is always a person’s decision, whichever way this is set.'}
          </Typography>

          {!settings.bondCourtAutoSend && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Recommended until you have watched a few go out. Whichever way this is set, a court
              with no usable email address gets nothing — those route to the internal LOR mailbox
              and still need sending by hand.
            </Alert>
          )}

          {/* The motion send (18 Sep meeting, items 1–2). Same rule as bonds, and hidden when the API
              does not report it — an unchecked switch would claim sending is off when that is
              merely unknown. */}
          {settings.motionCourtAutoSend !== undefined && (
            <>
              <Divider sx={{ my: 2 }} />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.motionCourtAutoSend}
                    onChange={(e) => void handleToggle('motionCourtAutoSend', e.target.checked)}
                    disabled={!settings.canManage || saving}
                  />
                }
                label="Send motions to courts automatically when staff press Send"
              />
              <Typography variant="body2" color="text.secondary">
                {settings.motionCourtAutoSend
                  ? 'Pressing Send on a motion emails the motion and its order straight to the court, if that court takes filings by email. Nobody else sees it before it goes.'
                  : 'Pressing Send on a motion prepares the email with the motion and its order attached, then leaves it in the Court Communication drafts for someone to send.'}
              </Typography>
              {!settings.motionCourtAutoSend && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Recommended until you have watched a few go out. Either way, a court that files by
                  website, fax or post — or has no usable email address — is never emailed; those go
                  to the internal LOR mailbox to be filed by hand.
                </Alert>
              )}
            </>
          )}

          {/* Lloyd, 2 Oct 2026: "Please start sending those out ASAP". Hidden when the API does not
              report it, for the same reason as the switches around it. */}
          {settings.staleCaseCourtAutoSend !== undefined && (
            <>
              <Divider sx={{ my: 2 }} />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.staleCaseCourtAutoSend}
                    onChange={(e) => void handleToggle('staleCaseCourtAutoSend', e.target.checked)}
                    disabled={!settings.canManage || saving}
                  />
                }
                label="Email courts for a status update on cases with no word for 90 days"
              />
              <Typography variant="body2" color="text.secondary">
                {settings.staleCaseCourtAutoSend
                  ? 'Each weekday morning up to 100 emails go out from the Court Communication mailbox, one per client per court, oldest first, asking whether the cases are closed. Replies come back to that mailbox.'
                  : 'Nothing is sent. The cases are listed under "Waiting on a court", where staff can draft the emails by hand.'}
              </Typography>
            </>
          )}

          {/* 18 Sep meeting, section 8: "Offer received → progress moves automatically". The switch
              existed since August with nothing reading it; hidden when the API does not report it,
              because an unchecked switch would claim the move is off when that is merely unknown. */}
          {settings.offerAutoStageMove !== undefined && (
            <>
              <Divider sx={{ my: 2 }} />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.offerAutoStageMove}
                    onChange={(e) => void handleToggle('offerAutoStageMove', e.target.checked)}
                    disabled={!settings.canManage || saving}
                  />
                }
                label="Move a case to “offer received” when an offer is recorded"
              />
              <Typography variant="body2" color="text.secondary">
                {settings.offerAutoStageMove
                  ? 'Recording an offer moves the case to “offer received” by itself. A case already past that point — accepted, in conditions or closed — is never moved back. The client is not emailed or texted because of the move.'
                  : 'Recording an offer leaves the case where it is. Staff move it with “Move this case to offer received” under the Offer box on the chart.'}
              </Typography>
            </>
          )}

          {/* The acceptance send (Lloyd, 29 Sep 2026: "Let's make it automatic now"). The one court
              send that ships on; hidden when the API does not report it, like the rest. */}
          {settings.acceptanceCourtAutoSend !== undefined && (
            <>
              <Divider sx={{ my: 2 }} />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.acceptanceCourtAutoSend}
                    onChange={(e) => void handleToggle('acceptanceCourtAutoSend', e.target.checked)}
                    disabled={!settings.canManage || saving}
                  />
                }
                label="Send the acceptance to the court automatically when staff press Accept"
              />
              <Typography variant="body2" color="text.secondary">
                {settings.acceptanceCourtAutoSend
                  ? 'Pressing Accept on an offer emails the acceptance straight to the court, if that court takes filings by email, and marks "acceptance sent" on the case chart.'
                  : 'Pressing Accept on an offer prepares the acceptance email and leaves it in the Court Communication drafts for someone to send.'}
              </Typography>
              <Alert severity="info" sx={{ mt: 1 }}>
                Either way, a court that files by website, fax or post — or has no usable email
                address — is never emailed; the acceptance goes to the internal LOR mailbox to be
                filed by hand. An appeal or jury trial offer never sends an acceptance.
              </Alert>
            </>
          )}

          {/* Both of these existed as settings with nothing able to flip them, so turning either
              on meant a hand-written row in app_setting.

              Hidden rather than shown as "off" when the API does not report them. An unchecked
              switch is a claim that sending is disabled, and an older API answering without the
              field is not evidence of that. Absent means unknown, and a control that states the
              wrong thing about client-facing sends is worse than one that is not there. */}
          {settings.portalInviteSend !== undefined && (
          <>
          <Divider sx={{ my: 2 }} />
          <FormControlLabel
            control={
              <Switch
                checked={settings.portalInviteSend === true}
                onChange={(e) => void handleToggle('portalInviteSend', e.target.checked)}
                disabled={!settings.canManage || saving}
              />
            }
            label="Send portal invitations to existing clients"
          />

          <Typography variant="body2" color="text.secondary">
            {settings.portalInviteSend
              ? 'The invitation script may send. It is the highest-volume client-facing send in the system, so run it as a dry run first and read the list.'
              : 'The invitation script refuses to send and reports what it would have done. Turn this on only once the wording has been approved.'}
          </Typography>
          </>
          )}

          {settings.messagesClientResend !== undefined && (
          <>
          <Divider sx={{ my: 2 }} />
          <FormControlLabel
            control={
              <Switch
                checked={settings.messagesClientResend === true}
                onChange={(e) => void handleToggle('messagesClientResend', e.target.checked)}
                disabled={!settings.canManage || saving}
              />
            }
            label="Send an unanswered message to the client again"
          />

          <Typography variant="body2" color="text.secondary">
            {settings.messagesClientResend
              ? 'A message the client has not answered is sent to them again after 24 hours, then 48, then weekly, by portal, email and text, until they reply or somebody closes the conversation.'
              : 'Nothing is sent to clients. Conversations still come back to the Messages tab on the same schedule for staff to chase by hand.'}
          </Typography>
          </>
          )}

          {settings.cdlEmailToCase !== undefined && (
          <>
          <Divider sx={{ my: 2 }} />
          <FormControlLabel
            control={
              <Switch
                checked={settings.cdlEmailToCase === true}
                onChange={(e) => void handleToggle('cdlEmailToCase', e.target.checked)}
                disabled={!settings.canManage || saving}
              />
            }
            label="File CDL Consultants email against cases"
          />

          <Typography variant="body2" color="text.secondary">
            {settings.cdlEmailToCase
              ? 'Their update requests are matched to a case and filed on it. Requests that cannot be matched are queued, and cleared through the API until a screen exists for them. Whether an answer is sent depends on the switch below.'
              : 'Their mailbox is not read. Update requests stay in the mailbox and nothing is filed.'}
          </Typography>
          </>
          )}

          {settings.cdlAutoReply !== undefined && (
          <>
          <Divider sx={{ my: 2 }} />
          <FormControlLabel
            control={
              <Switch
                checked={settings.cdlAutoReply === true}
                onChange={(e) => void handleToggle('cdlAutoReply', e.target.checked)}
                disabled={!settings.canManage || saving}
              />
            }
            label="Answer CDL Consultants automatically"
          />

          <Typography variant="body2" color="text.secondary">
            {settings.cdlAutoReply
              ? 'A matched request is answered with the case status, without a person reading it. Only where the client has agreed to their case being discussed with the referring organisation.'
              : 'Answers are prepared and left as drafts for a person to send. Nothing reaches CDL on its own.'}
          </Typography>
          </>
          )}

          {settings.failureToAppearFee !== undefined && (
          <>
          <Divider sx={{ my: 2 }} />
          <FormControlLabel
            control={
              <Switch
                checked={settings.failureToAppearFee === true}
                onChange={(e) => void handleToggle('failureToAppearFee', e.target.checked)}
                disabled={!settings.canManage || saving}
              />
            }
            label="Quote the failure-to-appear fee on cases"
          />

          <Typography variant="body2" color="text.secondary">
            {settings.failureToAppearFee
              ? 'Staff are shown what a missed setting would cost under Section IX, and the schedule itself. Nothing is charged automatically; a person still decides.'
              : 'No amount is shown on any case, and the schedule is hidden. Missed settings are still listed.'}
          </Typography>
          </>
          )}

          {/* The sentence that goes on top of the client's own message, which then follows
              verbatim. Kept as a setting so changing it is not a release. Left blank, the shipped
              sentence is used, which is why the default is the placeholder rather than the value. */}
          {settings.messagesResendPrefaceDefault !== undefined && (
          <TextField
            fullWidth
            multiline
            size="small"
            label="Line on top of a re-sent message"
            placeholder={settings.messagesResendPrefaceDefault}
            value={preface}
            onChange={(e) => setPreface(e.target.value)}
            onBlur={() => void savePreface()}
            disabled={!settings.canManage || saving}
            inputProps={{ maxLength: 200 }}
            helperText={
              preface.trim()
                ? `${preface.trim().length} of 200 characters. The client's own message follows underneath.`
                : `Blank, so this is used: "${settings.messagesResendPrefaceDefault}"`
            }
          />
          )}

          {/* The discovery wording. Shown only when something is waiting, because an approval
              nobody needs to give is a control staff learn to ignore. */}
          {settings.discoveryApproval && !settings.discoveryApproval.approved && (
            <Alert
              severity="warning"
              action={
                settings.canManage ? (
                  <Button
                    size="small"
                    disabled={saving}
                    onClick={() => void approveDiscoveryWording()}
                  >
                    Approve
                  </Button>
                ) : undefined
              }
            >
              {settings.discoveryApproval.explanation} Read the request before approving: the
              wording is the statutory basis of a document served on a court, and until it is
              approved the 39.14 request is left off the not-guilty letter. The letter and the
              preservation demand still go out.
            </Alert>
          )}

          {!settings.canManage && (
            <Typography variant="body2" color="text.secondary">
              You do not have permission to change this. Ask a manager with settings access.
            </Typography>
          )}

          {settings.updatedAt && (
            <Typography variant="caption" color="text.secondary">
              Court auto-send last changed
              {settings.updatedBy ? ` by ${settings.updatedBy}` : ''} on{' '}
              {new Date(settings.updatedAt).toLocaleString('en-US')}
            </Typography>
          )}
        </Box>
      ) : null}
    </Paper>
  );
};

export default AutomationSettingsPanel;
